import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import Chart from 'react-apexcharts';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './HotelDetailPage.css';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/marker-icon-2x.png',
  iconUrl: '/marker-icon.png',
  shadowUrl: '/marker-shadow.png',
});

// Helper component to get map reference
const MapHandler = ({ onMapReady }) => {
  const map = useMap();
  
  useEffect(() => {
    if (map) {
      onMapReady(map);
    }
  }, [map, onMapReady]);
  
  return null;
};

const HotelDetailPage = () => {
  const { hotelId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hotel, setHotel] = useState(null);
  const [hotelImages, setHotelImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [error, setError] = useState(null);
  
  // Search criteria state for editable navigation
  const [searchCriteria, setSearchCriteria] = useState({
    checkInDate: '',
    checkOutDate: '',
    adults: '',
    children: '',
    language: '',
    currency: ''
  });
  const [isEditingSearch, setIsEditingSearch] = useState(false);

  // Weather summary state (from HistoryPage functionality)
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(null);
  const [showWeatherSummary, setShowWeatherSummary] = useState(false);
  const [weatherChartData, setWeatherChartData] = useState(null);
  const [chartOptions, setChartOptions] = useState(null);

  // Map functionality state
  const [mapRef, setMapRef] = useState(null);
  const [showMapControls, setShowMapControls] = useState(false);

  // Function to handle back navigation with preserved search parameters
  const handleBackToSearch = () => {
    const searchParamsString = searchParams.toString();
    if (searchParamsString) {
      navigate(`/?${searchParamsString}`);
    } else {
      navigate('/');
    }
  };

  // Weather data fetching function (adapted from HistoryPage)
  const fetchWeatherSummary = async (latitude, longitude) => {
    try {
      setWeatherLoading(true);
      setWeatherError(null);
      
      // For now, use mock weather data based on location
      // In production, this would call the actual weather service
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      // Generate mock last year's temperature data (12 months)
      const generateLastYearData = () => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const baseTemp = 28; // Bangkok base temperature
        
        return months.map((month, index) => {
          // Simulate seasonal variation for Bangkok climate
          const seasonalVariation = Math.sin((index - 3) * Math.PI / 6) * 4; // Peak in May-Jun, low in Dec-Jan
          const randomVariation = (Math.random() - 0.5) * 3;
          const temperature = baseTemp + seasonalVariation + randomVariation;
          
          return {
            x: month,
            y: Math.round(temperature * 10) / 10
          };
        });
      };
      
      const lastYearData = generateLastYearData();
      
      // Mock weather data based on Bangkok coordinates (adjust as needed)
      const mockWeatherData = {
        avgTemp: "28.5",
        maxTemp: "32.1", 
        minTemp: "25.3",
        recentData: [], // Would contain actual weather history
        lastUpdated: new Date().toLocaleDateString()
      };
      
      // Chart configuration (adapted from HistoryPage)
      const chartOptions = {
        chart: {
          id: 'weather-chart',
          type: 'line',
          height: 300,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              pan: false,
              reset: false
            }
          },
          background: 'transparent'
        },
        colors: ['#14b8a6'], // Teal color matching our theme
        stroke: {
          width: 3,
          curve: 'smooth'
        },
        dataLabels: {
          enabled: false
        },
        markers: {
          size: 6,
          colors: ['#14b8a6'],
          strokeColors: '#ffffff',
          strokeWidth: 2,
          hover: {
            size: 8
          }
        },
        xaxis: {
          categories: lastYearData.map(item => item.x),
          labels: {
            style: {
              colors: '#cbd5e1',
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Temperature (°C)',
            style: {
              color: '#cbd5e1'
            }
          },
          labels: {
            style: {
              colors: '#cbd5e1'
            },
            formatter: function (value) {
              return value.toFixed(1) + '°C';
            }
          }
        },
        grid: {
          borderColor: 'rgba(148, 163, 184, 0.2)',
          strokeDashArray: 4
        },
        theme: {
          mode: 'dark'
        },
        tooltip: {
          theme: 'dark',
          y: {
            formatter: function (value) {
              return value.toFixed(1) + '°C';
            }
          }
        }
      };
      
      const chartData = [
        {
          name: 'Average Temperature',
          data: lastYearData.map(item => item.y)
        }
      ];
      
      setWeatherData(mockWeatherData);
      setWeatherChartData(chartData);
      setChartOptions(chartOptions);
      
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setWeatherError('Unable to load weather information');
    } finally {
      setWeatherLoading(false);
    }
  };

  // Google Maps integration function
  const openInGoogleMaps = () => {
    if (hotel?.location?.latitude && hotel?.location?.longitude) {
      const lat = parseFloat(hotel.location.latitude);
      const lng = parseFloat(hotel.location.longitude);
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}&query_place_id=${encodeURIComponent(hotel.hotelName)}`;
      window.open(googleMapsUrl, '_blank');
    }
  };

  // Function to center map on hotel pin
  const centerMapOnPin = () => {
    if (mapRef && hotel?.location?.latitude && hotel?.location?.longitude) {
      const lat = parseFloat(hotel.location.latitude);
      const lng = parseFloat(hotel.location.longitude);
      mapRef.setView([lat, lng], 17); // Zoom closer when centering
    }
  };

  // Function to handle map ready event
  const onMapReady = (map) => {
    setMapRef(map);
    setShowMapControls(true);
  };

  // Mock hotel data with price comparisons
  const mockHotelDetail = {
    hotelId: 1,
    hotelName: "Bangkok Palace Hotel",
    starRating: 5,
    reviewScore: 8.5,
    reviewCount: 1250,
    description: "Experience luxury at its finest in the heart of Bangkok. This 5-star hotel offers world-class amenities, exceptional service, and stunning city views. Located in the prestigious business district, you'll be steps away from shopping, dining, and entertainment.",
    address: "123 Sukhumvit Road, Watthana, Bangkok 10110, Thailand",
    images: [
      "/logo.jpeg",
      "/logo.jpeg",
      "/logo.jpeg",
      "/logo.jpeg",
      "/logo.jpeg"
    ],
    amenities: [
      { icon: "🏊‍♂️", name: "Swimming Pool", category: "Recreation" },
      { icon: "🍽️", name: "Restaurant", category: "Dining" },
      { icon: "📶", name: "Free WiFi", category: "Internet" },
      { icon: "🚗", name: "Parking", category: "Transportation" },
      { icon: "💪", name: "Fitness Center", category: "Recreation" },
      { icon: "🧖‍♀️", name: "Spa & Wellness", category: "Recreation" },
      { icon: "🏢", name: "Business Center", category: "Business" },
      { icon: "🛎️", name: "Concierge", category: "Service" },
      { icon: "🚿", name: "Room Service", category: "Service" },
      { icon: "❄️", name: "Air Conditioning", category: "Comfort" },
      { icon: "📺", name: "TV", category: "Entertainment" },
      { icon: "☕", name: "Mini Bar", category: "Dining" }
    ],
    priceComparisons: [
      {
        source: "Agoda",
        logo: "/logo.jpeg",
        originalPrice: 180,
        currentPrice: 150,
        currency: "USD",
        discountPercentage: 17,
        inclusions: ["Free WiFi", "Breakfast"],
        cancellationPolicy: "Free cancellation until 24 hours before check-in",
        bookingUrl: "https://agoda.com",
        rating: 9.2,
        isPartner: true
      },
      {
        source: "Booking.com",
        logo: "/logo.jpeg",
        originalPrice: 175,
        currentPrice: 155,
        currency: "USD",
        discountPercentage: 11,
        inclusions: ["Free WiFi"],
        cancellationPolicy: "Free cancellation until 48 hours before check-in",
        bookingUrl: "https://booking.com",
        rating: 8.9,
        isPartner: false
      },
      {
        source: "Expedia",
        logo: "/logo.jpeg",
        originalPrice: 170,
        currentPrice: 160,
        currency: "USD",
        discountPercentage: 6,
        inclusions: ["Free WiFi", "Airport Transfer"],
        cancellationPolicy: "Non-refundable",
        bookingUrl: "https://expedia.com",
        rating: 8.7,
        isPartner: false
      },
      {
        source: "Hotels.com",
        logo: "/logo.jpeg",
        originalPrice: 165,
        currentPrice: 165,
        currency: "USD",
        discountPercentage: 0,
        inclusions: ["Free WiFi"],
        cancellationPolicy: "Free cancellation until 24 hours before check-in",
        bookingUrl: "https://hotels.com",
        rating: 8.5,
        isPartner: false
      }
    ],
    location: {
      latitude: 13.7563,
      longitude: 100.5018,
      nearbyAttractions: [
        { name: "MBK Center", distance: "0.3 km", type: "Shopping" },
        { name: "Siam Paragon", distance: "0.5 km", type: "Shopping" },
        { name: "BTS Siam Station", distance: "0.2 km", type: "Transportation" },
        { name: "Jim Thompson House", distance: "1.2 km", type: "Culture" }
      ]
    },
    rooms: [
      {
        type: "Deluxe Room",
        size: "32 sqm",
        maxGuests: 2,
        bedType: "King Bed",
        features: ["City View", "Free WiFi", "Air Conditioning"]
      },
      {
        type: "Executive Suite",
        size: "65 sqm",
        maxGuests: 4,
        bedType: "King Bed + Sofa Bed",
        features: ["Executive Lounge Access", "City View", "Kitchenette"]
      }
    ]
  };

  // Function to fetch hotel details from the API
  const fetchHotelDetails = async (hotelId, searchCriteria = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      // Build search criteria from URL parameters or defaults
      const defaultCriteria = {
        checkInDate: "2025-10-10",
        checkOutDate: "2025-10-12",
        currency: "USD",
        language: "en-us",
        maxResult: 10
      };
      
      // Extract search criteria from URL search params
      const urlCriteria = {};
      if (searchParams.get('checkInDate')) urlCriteria.checkInDate = searchParams.get('checkInDate');
      if (searchParams.get('checkOutDate')) urlCriteria.checkOutDate = searchParams.get('checkOutDate');
      if (searchParams.get('currency')) urlCriteria.currency = searchParams.get('currency');
      if (searchParams.get('language')) urlCriteria.language = searchParams.get('language');
      if (searchParams.get('maxResult')) urlCriteria.maxResult = parseInt(searchParams.get('maxResult'));
      
      const finalCriteria = { ...defaultCriteria, ...urlCriteria, ...searchCriteria };
      
      const requestBody = {
        hotelId: parseInt(hotelId),
        searchCriteria: {
          criteria: {
            additional: {
              currency: finalCriteria.currency,
              language: finalCriteria.language,
              maxResult: finalCriteria.maxResult
            },
            checkInDate: finalCriteria.checkInDate,
            checkOutDate: finalCriteria.checkOutDate,
            hotelId: [parseInt(hotelId)]
          }
        }
      };

      console.log('Fetching hotel details with payload:', requestBody);

      const response = await fetch('https://7juj2p3or5.execute-api.ap-southeast-7.amazonaws.com/Prod/hotel-detail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch hotel details: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Hotel details API response:', data);

      if (data) {
        // Transform API response to match our component structure
        const transformedHotel = {
          hotelId: data.hotelId || hotelId,
          hotelName: data.hotelName || "Hotel Name Not Available",
          starRating: data.starRating || 0,
          reviewScore: data.reviewScore || 0,
          reviewCount: data.reviewCount || 0,
          description: data.description || data.hotelDescription || "Description not available",
          address: data.address || data.location?.address || "Address not available",
          images: data.images || ["/logo.jpeg"],
          amenities: data.amenities || [],
          priceComparisons: transformPriceComparisons(data.priceComparisons || []),
          location: {
            latitude: data.latitude || data.location?.latitude || 13.7563,
            longitude: data.longitude || data.location?.longitude || 100.5018,
            nearbyAttractions: data.location?.nearbyAttractions || []
          },
          rooms: data.rooms || []
        };
        
        setHotel(transformedHotel);
        setHotelImages(transformedHotel.images || []);
      } else if (data && data.error) {
        // Handle API error response
        setError(data.error);
        setHotel(null); // Don't fallback to mock data, show not found
      } else {
        // Don't fallback to mock data if API doesn't return expected structure
        console.warn('API response does not contain expected hotelDetail');
        setError('Hotel not found in our database');
        setHotel(null);
      }
    } catch (error) {
      console.error('Error fetching hotel details:', error);
      setError(error.message);
      // Don't fallback to mock data on error
      setHotel(null);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to transform price comparisons from API response
  const transformPriceComparisons = (priceComparisons) => {
    return priceComparisons.map(price => ({
      source: price.source || "Unknown",
      logo: price.logo || "/logo.jpeg",
      originalPrice: price.originalPrice || 0,
      currentPrice: price.currentPrice || price.price || 0,
      currency: price.currency || "USD",
      discountPercentage: price.discountPercentage || 0,
      inclusions: price.inclusions || [],
      cancellationPolicy: price.cancellationPolicy || "Standard policy",
      bookingUrl: price.bookingUrl || "#",
      rating: price.rating || 0,
      isPartner: price.isPartner || false
    }));
  };

  useEffect(() => {
    if (hotelId) {
      // Fetch hotel details from API
      fetchHotelDetails(hotelId);
    }
  }, [hotelId, searchParams]);

  // Initialize search criteria from URL parameters
  useEffect(() => {
    setSearchCriteria({
      checkInDate: searchParams.get('checkInDate') || '2025-10-10',
      checkOutDate: searchParams.get('checkOutDate') || '2025-10-12',
      adults: searchParams.get('adults') || '2',
      children: searchParams.get('children') || '0',
      language: searchParams.get('language') || 'en-us',
      currency: searchParams.get('currency') || 'USD'
    });
  }, [searchParams]);

  // Fetch weather data when hotel location is available
  useEffect(() => {
    if (hotel?.location?.latitude && hotel?.location?.longitude && showWeatherSummary) {
      fetchWeatherSummary(
        parseFloat(hotel.location.latitude), 
        parseFloat(hotel.location.longitude)
      );
    }
  }, [hotel?.location, showWeatherSummary]);

  // Language options based on Agoda API (same as HotelSearch)
  const languageOptions = [
    { value: 'en-us', label: 'English (US)' },
    { value: 'fr-fr', label: 'Français' },
    { value: 'de-de', label: 'Deutsch' },
    { value: 'it-it', label: 'Italiano' },
    { value: 'es-es', label: 'Español' },
    { value: 'ja-jp', label: '日本語' },
    { value: 'zh-hk', label: '繁體中文 (香港)' },
    { value: 'zh-cn', label: '简体中文' },
    { value: 'ko-kr', label: '한국어' },
    { value: 'el-gr', label: 'Ελληνικά' },
    { value: 'ru-ru', label: 'Русский' },
    { value: 'pt-pt', label: 'Português' },
    { value: 'nl-nl', label: 'Nederlands' },
    { value: 'en-ca', label: 'English (Canada)' },
    { value: 'en-in', label: 'English (India)' },
    { value: 'en-gb', label: 'English (UK)' },
    { value: 'en-za', label: 'English (South Africa)' },
    { value: 'en-au', label: 'English (Australia)' },
    { value: 'en-sg', label: 'English (Singapore)' },
    { value: 'zh-tw', label: '繁體中文 (台灣)' },
    { value: 'en-nz', label: 'English (New Zealand)' },
    { value: 'th-th', label: 'ไทย' },
    { value: 'ms-my', label: 'Bahasa Malaysia' },
    { value: 'vi-vn', label: 'Tiếng Việt' },
    { value: 'sv-se', label: 'Svenska' },
    { value: 'id-id', label: 'Bahasa Indonesia' },
    { value: 'pl-pl', label: 'Polski' },
    { value: 'nb-no', label: 'Norsk' },
    { value: 'da-dk', label: 'Dansk' },
    { value: 'fi-fi', label: 'Suomi' },
    { value: 'cs-cz', label: 'Čeština' },
    { value: 'tr-tr', label: 'Türkçe' },
    { value: 'ca-es', label: 'Català' },
    { value: 'hu-hu', label: 'Magyar' },
    { value: 'hi-in', label: 'हिन्दी' },
    { value: 'bg-bg', label: 'Български' },
    { value: 'ro-ro', label: 'Română' },
    { value: 'sl-si', label: 'Slovenščina' },
    { value: 'he-il', label: 'עברית' },
    { value: 'ar-ae', label: 'العربية' },
    { value: 'nl-be', label: 'Nederlands (België)' },
    { value: 'en-ie', label: 'English (Ireland)' },
    { value: 'pt-br', label: 'Português (Brasil)' },
    { value: 'es-ar', label: 'Español (Argentina)' },
    { value: 'es-mx', label: 'Español (México)' },
    { value: 'lt-lt', label: 'Lietuvių' },
    { value: 'lv-lv', label: 'Latviešu' },
    { value: 'hr-hr', label: 'Hrvatski' },
    { value: 'et-ee', label: 'Eesti' },
    { value: 'uk-ua', label: 'Українська' }
  ];

  // Currency options based on Agoda API (same as HotelSearch)
  const currencyOptions = [
    { value: 'USD', label: 'USD ($)', symbol: '$' },
    { value: 'EUR', label: 'EUR (€)', symbol: '€' },
    { value: 'GBP', label: 'GBP (£)', symbol: '£' },
    { value: 'HKD', label: 'HKD (HK$)', symbol: 'HK$' },
    { value: 'MYR', label: 'MYR (RM)', symbol: 'RM' },
    { value: 'SGD', label: 'SGD (S$)', symbol: 'S$' },
    { value: 'THB', label: 'THB (฿)', symbol: '฿' },
    { value: 'NZD', label: 'NZD (NZ$)', symbol: 'NZ$' },
    { value: 'AUD', label: 'AUD (A$)', symbol: 'A$' },
    { value: 'JPY', label: 'JPY (¥)', symbol: '¥' },
    { value: 'ZAR', label: 'ZAR (R)', symbol: 'R' },
    { value: 'CAD', label: 'CAD (C$)', symbol: 'C$' },
    { value: 'AED', label: 'AED (د.إ)', symbol: 'د.إ' },
    { value: 'CNY', label: 'CNY (¥)', symbol: '¥' },
    { value: 'PHP', label: 'PHP (₱)', symbol: '₱' },
    { value: 'CHF', label: 'CHF (Fr)', symbol: 'Fr' },
    { value: 'DKK', label: 'DKK (kr)', symbol: 'kr' },
    { value: 'SEK', label: 'SEK (kr)', symbol: 'kr' },
    { value: 'CZK', label: 'CZK (Kč)', symbol: 'Kč' },
    { value: 'PLN', label: 'PLN (zł)', symbol: 'zł' },
    { value: 'IDR', label: 'IDR (Rp)', symbol: 'Rp' },
    { value: 'KRW', label: 'KRW (₩)', symbol: '₩' },
    { value: 'INR', label: 'INR (₹)', symbol: '₹' },
    { value: 'TWD', label: 'TWD (NT$)', symbol: 'NT$' },
    { value: 'NOK', label: 'NOK (kr)', symbol: 'kr' },
    { value: 'OMR', label: 'OMR (ر.ع.)', symbol: 'ر.ع.' },
    { value: 'FJD', label: 'FJD (FJ$)', symbol: 'FJ$' },
    { value: 'BHD', label: 'BHD (.د.ب)', symbol: '.د.ب' },
    { value: 'ARS', label: 'ARS ($)', symbol: '$' },
    { value: 'XPF', label: 'XPF (₣)', symbol: '₣' },
    { value: 'VND', label: 'VND (₫)', symbol: '₫' },
    { value: 'HUF', label: 'HUF (Ft)', symbol: 'Ft' },
    { value: 'UAH', label: 'UAH (₴)', symbol: '₴' },
    { value: 'JOD', label: 'JOD (د.ا)', symbol: 'د.ا' },
    { value: 'KWD', label: 'KWD (د.ك)', symbol: 'د.ك' },
    { value: 'MXN', label: 'MXN ($)', symbol: '$' },
    { value: 'NGN', label: 'NGN (₦)', symbol: '₦' },
    { value: 'ILS', label: 'ILS (₪)', symbol: '₪' },
    { value: 'PKR', label: 'PKR (₨)', symbol: '₨' },
    { value: 'QAR', label: 'QAR (ر.ق)', symbol: 'ر.ق' },
    { value: 'SAR', label: 'SAR (ر.س)', symbol: 'ر.س' },
    { value: 'BRL', label: 'BRL (R$)', symbol: 'R$' },
    { value: 'RON', label: 'RON (lei)', symbol: 'lei' },
    { value: 'BGN', label: 'BGN (лв)', symbol: 'лв' },
    { value: 'TRY', label: 'TRY (₺)', symbol: '₺' },
    { value: 'EGP', label: 'EGP (£)', symbol: '£' },
    { value: 'LKR', label: 'LKR (₨)', symbol: '₨' },
    { value: 'BDT', label: 'BDT (৳)', symbol: '৳' },
    { value: 'MMK', label: 'MMK (K)', symbol: 'K' }
  ];

  // Handle search criteria updates
  const handleSearchUpdate = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Apply updated search criteria
  const applySearchChanges = () => {
    const newParams = new URLSearchParams();
    Object.entries(searchCriteria).forEach(([key, value]) => {
      if (value) newParams.set(key, value);
    });
    
    // Navigate to updated URL and refetch hotel details
    navigate(`/hotel/${hotelId}?${newParams.toString()}`, { replace: true });
    setIsEditingSearch(false);
  };

  // Cancel search editing
  const cancelSearchEdit = () => {
    // Reset to current URL params
    setSearchCriteria({
      checkInDate: searchParams.get('checkInDate') || '2025-10-10',
      checkOutDate: searchParams.get('checkOutDate') || '2025-10-12',
      adults: searchParams.get('adults') || '2',
      children: searchParams.get('children') || '0',
      language: searchParams.get('language') || 'en-us',
      currency: searchParams.get('currency') || 'USD'
    });
    setIsEditingSearch(false);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span key={index} className={`star ${index < rating ? 'filled' : ''}`}>★</span>
    ));
  };

  const getReviewScoreText = (score) => {
    if (score >= 9) return "Exceptional";
    if (score >= 8) return "Excellent";
    if (score >= 7) return "Very Good";
    if (score >= 6) return "Good";
    return "Fair";
  };

  const getReviewScoreColor = (score) => {
    if (score >= 9) return "#059669";
    if (score >= 8) return "#16a34a";
    if (score >= 7) return "#65a30d";
    if (score >= 6) return "#ca8a04";
    return "#dc2626";
  };

  const getCurrencySymbol = (currency) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', THB: '฿' };
    return symbols[currency] || currency;
  };

  const getBestPrice = () => {
    if (!hotel?.priceComparisons || hotel.priceComparisons.length === 0) return null;
    const cheapestPrice = hotel.priceComparisons.reduce((min, price) => 
      price.currentPrice < min.currentPrice ? price : min
    );
    return cheapestPrice;
  };

  const groupAmenitiesByCategory = () => {
    if (!hotel?.amenities) return {};
    return hotel.amenities.reduce((acc, amenity) => {
      if (!acc[amenity.category]) acc[amenity.category] = [];
      acc[amenity.category].push(amenity);
      return acc;
    }, {});
  };

  if (loading) {
    return (
      <div className="hotel-detail-loading">
        <div className="loading-animation">
          <div className="hotel-icon">🏨</div>
          <div className="loading-bars">
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </div>
        </div>
        <h3>Loading Hotel Details...</h3>
      </div>
    );
  }

  if (!hotel) {
    return (
      <div className="hotel-detail-container">
        <button onClick={handleBackToSearch} className="back-button">
          ← Back to Results
        </button>
        <div className="hotel-detail-error">
          <h2>Hotel Not Found</h2>
          <p>{error || "Sorry, we couldn't find the hotel you're looking for."}</p>
          <button onClick={handleBackToSearch} className="back-btn">
            Back to Search
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-detail-container">
      {/* Back Button */}
      <button onClick={handleBackToSearch} className="back-button">
        ← Back to Results
      </button>

      {/* Editable Search Navigation Bar */}
      <div className="search-navigation-bar">
        <div className="search-nav-content">
          {isEditingSearch ? (
            // Edit Mode
            <>
              <div className="search-nav-item">
                <span className="search-nav-icon">📅</span>
                <div className="search-nav-field">
                  <label>Check-in</label>
                  <input
                    type="date"
                    value={searchCriteria.checkInDate}
                    onChange={(e) => handleSearchUpdate('checkInDate', e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              
              <div className="search-nav-item">
                <span className="search-nav-icon">📅</span>
                <div className="search-nav-field">
                  <label>Check-out</label>
                  <input
                    type="date"
                    value={searchCriteria.checkOutDate}
                    onChange={(e) => handleSearchUpdate('checkOutDate', e.target.value)}
                    className="search-input"
                  />
                </div>
              </div>
              
              <div className="search-nav-item">
                <span className="search-nav-icon">👥</span>
                <div className="search-nav-field">
                  <label>Adults</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={searchCriteria.adults}
                    onChange={(e) => handleSearchUpdate('adults', e.target.value)}
                    className="search-input search-input-small"
                  />
                </div>
              </div>
              
              <div className="search-nav-item">
                <span className="search-nav-icon">👶</span>
                <div className="search-nav-field">
                  <label>Children</label>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={searchCriteria.children}
                    onChange={(e) => handleSearchUpdate('children', e.target.value)}
                    className="search-input search-input-small"
                  />
                </div>
              </div>
              
              <div className="search-nav-item">
                <span className="search-nav-icon">🌐</span>
                <div className="search-nav-field">
                  <label>Language</label>
                  <select
                    value={searchCriteria.language}
                    onChange={(e) => handleSearchUpdate('language', e.target.value)}
                    className="search-input"
                  >
                    {languageOptions.map(lang => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="search-nav-item">
                <span className="search-nav-icon">💱</span>
                <div className="search-nav-field">
                  <label>Currency</label>
                  <select
                    value={searchCriteria.currency}
                    onChange={(e) => handleSearchUpdate('currency', e.target.value)}
                    className="search-input"
                  >
                    {currencyOptions.map(curr => (
                      <option key={curr.value} value={curr.value}>
                        {curr.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="search-nav-actions">
                <button onClick={applySearchChanges} className="search-btn search-btn-apply">
                  Apply
                </button>
                <button onClick={cancelSearchEdit} className="search-btn search-btn-cancel">
                  Cancel
                </button>
              </div>
            </>
          ) : (
            // Display Mode
            <>
              <div className="search-nav-item" onClick={() => setIsEditingSearch(true)}>
                <span className="search-nav-icon">📅</span>
                <div className="search-nav-display">
                  <span className="search-nav-label">Check-in</span>
                  <span className="search-nav-value">{searchCriteria.checkInDate}</span>
                </div>
              </div>
              
              <div className="search-nav-item" onClick={() => setIsEditingSearch(true)}>
                <span className="search-nav-icon">📅</span>
                <div className="search-nav-display">
                  <span className="search-nav-label">Check-out</span>
                  <span className="search-nav-value">{searchCriteria.checkOutDate}</span>
                </div>
              </div>
              
              <div className="search-nav-item" onClick={() => setIsEditingSearch(true)}>
                <span className="search-nav-icon">👥</span>
                <div className="search-nav-display">
                  <span className="search-nav-label">Guests</span>
                  <span className="search-nav-value">
                    {searchCriteria.adults} Adults
                    {parseInt(searchCriteria.children) > 0 && `, ${searchCriteria.children} Children`}
                  </span>
                </div>
              </div>
              
              <div className="search-nav-item" onClick={() => setIsEditingSearch(true)}>
                <span className="search-nav-icon">🌐</span>
                <div className="search-nav-display">
                  <span className="search-nav-label">Language</span>
                  <span className="search-nav-value">
                    {languageOptions.find(lang => lang.value === searchCriteria.language)?.label || searchCriteria.language.toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="search-nav-item" onClick={() => setIsEditingSearch(true)}>
                <span className="search-nav-icon">💱</span>
                <div className="search-nav-display">
                  <span className="search-nav-label">Currency</span>
                  <span className="search-nav-value">
                    {currencyOptions.find(curr => curr.value === searchCriteria.currency)?.label || searchCriteria.currency}
                  </span>
                </div>
              </div>
              
              <button onClick={() => setIsEditingSearch(true)} className="search-nav-edit-btn">
                ✏️ Edit Search
              </button>
            </>
          )}
        </div>
      </div>

      {/* Hotel Header */}
      <div className="hotel-detail-header">
        <div className="hotel-main-info">
          <h1 className="hotel-detail-name">{hotel.hotelName}</h1>
          <div className="hotel-detail-rating">
            <div className="stars">{renderStars(hotel.starRating)}</div>
            <div className="review-info">
              <span 
                className="review-score"
                style={{ backgroundColor: getReviewScoreColor(hotel.reviewScore) }}
              >
                {hotel.reviewScore}
              </span>
              <div className="review-details">
                <span className="review-text">{getReviewScoreText(hotel.reviewScore)}</span>
                <span className="review-count">({hotel.reviewCount} reviews)</span>
              </div>
            </div>
          </div>
          {/* <p className="hotel-address">📍 {hotel.address}</p> */}
        </div>

        <div className="best-price-highlight">
          <span className="best-price-label">Best Price</span>
          <span className="best-price-amount">
            {getBestPrice() ? `${getCurrencySymbol(getBestPrice().currency)}${getBestPrice().currentPrice}` : 'N/A'}
          </span>
          <span className="best-price-period">per night</span>
        </div>
      </div>

      {/* Image Gallery */}
      <div className="image-gallery">
        {imageLoading ? (
          <div className="image-loading">
            <div className="main-image-skeleton">
              <div className="skeleton-loader"></div>
            </div>
            <div className="thumbnails-skeleton">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="thumbnail-skeleton">
                  <div className="skeleton-loader"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="main-image">
              <img 
                src={hotelImages.length > 0 ? (typeof hotelImages[selectedImageIndex] === 'string' ? hotelImages[selectedImageIndex] : hotelImages[selectedImageIndex]?.url) : "/logo.jpeg"} 
                alt={`${hotel.hotelName} ${selectedImageIndex + 1}`}
                className="hotel-main-image"
              />
            </div>
            <div className="image-thumbnails">
              {hotelImages.map((image, index) => (
                <img
                  key={index}
                  src={typeof image === 'string' ? image : image.thumbnail || image.url}
                  alt={`${hotel.hotelName} ${index + 1}`}
                  className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Location Map */}
      {hotel?.location?.latitude && hotel?.location?.longitude && (
        <div className="location-section">
          <div className="location-header">
            <h2>Hotel Location</h2>
            <div className="location-actions">
              <button 
                className="find-pin-btn"
                onClick={centerMapOnPin}
                title="Find Hotel Pin"
              >
                📍 Find Pin
              </button>
              <button 
                className="google-maps-btn"
                onClick={openInGoogleMaps}
                title="Open in Google Maps"
              >
                🌍 Google Maps
              </button>
            </div>
          </div>
          
            <MapContainer
              center={[parseFloat(hotel.location.latitude), parseFloat(hotel.location.longitude)]}
              zoom={15}
              style={{ height: '400px', width: '100%' }}
              className="map-container"
            >
              <MapHandler onMapReady={onMapReady} />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <Marker position={[parseFloat(hotel.location.latitude), parseFloat(hotel.location.longitude)]}>
                <Popup>
                  <div className="custom-popup">
                    <strong>{hotel.hotelName}</strong><br/>
                    <span className="popup-address">{hotel.address}</span>
                    <div className="popup-actions">
                      <button 
                        className="popup-directions-btn"
                        onClick={openInGoogleMaps}
                      >
                        🧭 Get Directions
                      </button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
            
          
          {/* <div className="location-info">
            <p className="map-address">📍 {hotel.address}</p>
            <div className="location-features">
              <div className="location-feature">
                <span className="feature-icon">🎯</span>
                <span className="feature-text">Precise location on map</span>
              </div>
              <div className="location-feature">
                <span className="feature-icon">🚗</span>
                <span className="feature-text">
                  <button 
                    className="inline-directions-btn"
                    onClick={openInGoogleMaps}
                  >
                    Get driving directions
                  </button>
                </span>
              </div>
            </div>
          </div> */}
        </div>
      )}

      {/* Weather Summary Section */}
      <div className="weather-summary-section">
        <div className="weather-summary-header">
          <h2>Local Weather Summary</h2>
          <button 
            className={`weather-toggle-btn ${showWeatherSummary ? 'active' : ''}`}
            onClick={() => setShowWeatherSummary(!showWeatherSummary)}
          >
            {showWeatherSummary ? '🌤️ Hide Weather' : '🌦️ Show Weather'}
          </button>
        </div>
        
        {showWeatherSummary && (
          <div className="weather-summary-content">
            {weatherLoading ? (
              <div className="weather-loading">
                <div className="loading-spinner"></div>
                Loading weather information...
              </div>
            ) : weatherError ? (
              <div className="weather-error">
                ⚠️ {weatherError}
              </div>
            ) : weatherData ? (
              <div className="weather-stats">
                <div className="weather-overview">
                  <p className="weather-subtitle">7-day temperature summary for this location</p>
                  <div className="weather-stats-grid">
                    <div className="weather-stat-item">
                      <span className="weather-icon">🌡️</span>
                      <div className="weather-stat-info">
                        <span className="weather-stat-label">Average</span>
                        <span className="weather-stat-value">{weatherData.avgTemp}°C</span>
                      </div>
                    </div>
                    <div className="weather-stat-item">
                      <span className="weather-icon">🔥</span>
                      <div className="weather-stat-info">
                        <span className="weather-stat-label">High</span>
                        <span className="weather-stat-value">{weatherData.maxTemp}°C</span>
                      </div>
                    </div>
                    <div className="weather-stat-item">
                      <span className="weather-icon">❄️</span>
                      <div className="weather-stat-info">
                        <span className="weather-stat-label">Low</span>
                        <span className="weather-stat-value">{weatherData.minTemp}°C</span>
                      </div>
                    </div>
                  </div>
                  <p className="weather-last-updated">Last updated: {weatherData.lastUpdated}</p>
                </div>
                
                {/* Last Year Temperature Chart */}
                {weatherChartData && chartOptions && (
                  <div className="weather-chart-section">
                    <h4>Last Year's Temperature Trend</h4>
                    <p className="chart-subtitle">Monthly average temperatures for this location</p>
                    <div className="weather-chart">
                      <Chart
                        options={chartOptions}
                        series={weatherChartData}
                        type="line"
                        height={300}
                      />
                    </div>
                  </div>
                )}
                
                <div className="weather-tips">
                  <h4>Travel Tips</h4>
                  <ul>
                    <li>🧳 Pack according to temperature range: {weatherData.minTemp}°C - {weatherData.maxTemp}°C</li>
                    <li>☂️ Check current weather before your trip for any changes</li>
                    <li>🌡️ Average temperature is {weatherData.avgTemp}°C - plan indoor/outdoor activities accordingly</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="weather-placeholder">
                Click "Show Weather" to see climate information for this location
              </div>
            )}
          </div>
        )}
      </div>

      {/* Content Grid */}
      <div className="hotel-detail-content">
        {/* Left Column */}
        <div className="hotel-detail-left">
          {/* Description */}
          <div className="hotel-description">
            <h2>About This Hotel</h2>
            <p>{hotel.description}</p>
          </div>

          {/* Amenities */}
          {/* <div className="hotel-amenities-detail">
            <h2>Amenities & Services</h2>
            <div className="amenities-grid">
              {Object.entries(groupAmenitiesByCategory()).map(([category, amenities]) => (
                <div key={category} className="amenity-category">
                  <h4>{category}</h4>
                  <div className="amenity-list">
                    {amenities.slice(0, showAllAmenities ? amenities.length : 3).map((amenity, index) => (
                      <div key={index} className="amenity-item">
                        <span className="amenity-icon">{amenity.icon}</span>
                        <span className="amenity-name">{amenity.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {!showAllAmenities && (
              <button 
                className="show-more-amenities"
                onClick={() => setShowAllAmenities(true)}
              >
                Show All Amenities
              </button>
            )}
          </div> */}

          {/* Location & Nearby */}
          {/* <div className="hotel-location-detail">
            <h2>Location & Nearby</h2>
            <div className="nearby-attractions">
              {hotel.location.nearbyAttractions.map((attraction, index) => (
                <div key={index} className="attraction-item">
                  <span className="attraction-name">{attraction.name}</span>
                  <span className="attraction-distance">{attraction.distance}</span>
                  <span className="attraction-type">{attraction.type}</span>
                </div>
              ))}
            </div>
          </div> */}
        </div>

        {/* Right Column - Price Comparison */}
        <div className="hotel-detail-right">
          <div className="price-comparison-section">
            <h2>Compare Prices</h2>
            <p className="price-comparison-subtitle">
              Find the best deal from {hotel.priceComparisons.length} booking sites
            </p>
            
            <div className="price-comparison-list">
              {hotel.priceComparisons
                .sort((a, b) => a.currentPrice - b.currentPrice)
                .map((price, index) => (
                <div key={index} className={`price-comparison-item ${index === 0 ? 'best-deal' : ''}`}>
                  {index === 0 && <div className="best-deal-badge">🏆 Best Deal</div>}
                  {price.isPartner && <div className="partner-badge">Partner</div>}
                  
                  <div className="price-source-info">
                    <img src={price.logo} alt={price.source} className="source-logo" />
                    <div className="source-details">
                      <h4 className="source-name">{price.source}</h4>
                      <div className="source-rating">
                        ⭐ {price.rating}/10
                      </div>
                    </div>
                  </div>

                  <div className="price-details">
                    <div className="price-amount">
                      {price.originalPrice > price.currentPrice && (
                        <span className="original-price">
                          {getCurrencySymbol(price.currency)}{price.originalPrice}
                        </span>
                      )}
                      <span className="current-price">
                        {getCurrencySymbol(price.currency)}{price.currentPrice}
                      </span>
                      <span className="price-period">per night</span>
                    </div>
                    
                    {price.discountPercentage > 0 && (
                      <div className="discount-info">
                        Save {price.discountPercentage}%
                      </div>
                    )}

                    <div className="inclusions">
                      {price.inclusions.map((inclusion, idx) => (
                        <span key={idx} className="inclusion-tag">
                          ✓ {inclusion}
                        </span>
                      ))}
                    </div>

                    <div className="cancellation-policy">
                      <small>{price.cancellationPolicy}</small>
                    </div>
                  </div>

                  <a 
                    href={price.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="book-now-btn"
                  >
                    Book Now
                  </a>
                </div>
              ))}
            </div>

            {/* Price Disclaimer */}
            <div className="price-disclaimer">
              <p>
                <small>
                  ⚠️ Prices are provided by partners and may not include all taxes and fees. 
                  Final price will be confirmed during booking.
                </small>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotelDetailPage;